import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FormikProps } from "formik";
import { Autocomplete, Box } from "@mui/material";
import { Col, Form, Row } from "react-bootstrap";
import BootstrapTextField from "Common/Components/InputComponent/BootstrapTextField";
import { AppDispatch } from "index";
import { fetchCompanyByDropDown } from "slices/Masters/company/thunk";
import { createSelector } from "reselect";
import { MecordColors } from "Constants/Color";
import { autocompleteDropdownStyles } from "Constants/Style";

interface Company {
  company_id: string;
  company_name: string;
  vendor_code?: string | null;
  branch_name?: string | null;
  industry: string | null;
  gst_type: string;
  gstin: string;
  contacts: {
    contact_id: string;
    name: string;
    designation: string;
    phone_number: string;
    email: string;
  }[];
}

interface ContactDetail {
  contact_id: string;
  name: string;
  designation: string;
  phone_number: string;
  email: string;
}

interface CustomerDetailsSectionProps {
  formik: FormikProps<any>;
}

const CustomerDetailsSection: React.FC<CustomerDetailsSectionProps> = ({
  formik,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [companies, setCompanies] = useState<Company[]>([]);

  // Selector for company dropdown data
  const selectCompanyData = createSelector(
    (state: any) => state.Company,
    (company) => ({
      companiesData: company.companyList || [],
    })
  );

  const { companiesData } = useSelector(selectCompanyData);

  // Update companies state when companiesData is fetched
  useEffect(() => {
    setCompanies(companiesData);
  }, [companiesData]);

  // Fetch company dropdown
  useEffect(() => {
    dispatch(fetchCompanyByDropDown());
  }, [dispatch]);

  // Handle autocomplete change for company and contact person
  const handleAutocompleteChange = (name: string, value: any) => {
    if (name === "company") {
      // Clear previous contact details when company changes
      formik.setValues({
        ...formik.values,
        company: value,
        contactPerson: [],
        industry: value?.industry || "",
        type: value?.gst_type || "",
      });
    } else if (name === "contactPerson") {
      // Update contact persons array (multiselect)
      const contactPersons = Array.isArray(value) ? value : [];
      formik.setFieldValue("contactPerson", contactPersons);
    } else {
      formik.setFieldValue(name as string, value);
    }
  };

  // Get contact person value directly from formik
  const contactPersonValue = React.useMemo(() => {
    const cp = formik.values.contactPerson;
    if (Array.isArray(cp)) {
      // Filter out any invalid entries
      return cp.filter((c: ContactDetail) => c && (c.contact_id || c.email));
    }
    return [];
  }, [formik.values.contactPerson]);

  return (
    <div
      className="p-2 rounded mb-3"
      style={{ backgroundColor: MecordColors?.LightThemeBGColor }}
    >
      <Row className="g-3">
        <Col md={12}>
          <Form.Group>
            <Form.Label>
              Select Customer <span className="text-danger">*</span>
            </Form.Label>
            <Autocomplete
              options={companies}
              getOptionLabel={(option) =>
                option.company_name || option.company_id || ""
              }
              isOptionEqualToValue={(option, value) =>
                option?.company_id === value?.company_id
              }
              value={formik.values.company}
              onChange={(_, newValue) =>
                handleAutocompleteChange("company", newValue)
              }
              onBlur={() => {
                formik.setFieldTouched("company", true);
              }}
              ListboxProps={autocompleteDropdownStyles.ListboxProps}
              renderOption={(props, option) => {
                const { key, ...otherProps } = props;
                return (
                  <li key={key} {...otherProps} style={{ padding: 0 }}>
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.8,
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        cursor: "pointer",
                      }}
                    >
                      <span>{option.company_name || option.company_id || ""}</span>
                      {option.branch_name && (
                        <span
                          style={{
                            fontSize: "0.75rem",
                            color: "#6c757d",
                            marginTop: 2,
                          }}
                        >
                          {option.branch_name}
                        </span>
                      )}
                    </Box>
                  </li>
                );
              }}
              renderInput={(params) => (
                <BootstrapTextField
                  {...params}
                  name="company"
                  placeholder="Search Customer, Company Name"
                  size="small"
                  sx={autocompleteDropdownStyles.inputSx}
                  error={!!(formik.errors.company && formik.touched.company)}
                  helperText={
                    formik.errors.company && formik.touched.company
                      ? (formik.errors.company as string)
                      : ""
                  }
                />
              )}
            />
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group>
            <Form.Label>
              Industry 
              {/* <span className="text-danger">*</span> */}
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Autofill"
              value={
                formik.values?.company?.industry ||
                formik.values?.industry ||
                ""
              }
              disabled
              isInvalid={!!(formik.errors.industry && formik.touched.industry)}
            />
            {/* {formik.errors.industry && formik.touched.industry && (
              <Form.Control.Feedback type="invalid">
                {formik.errors.industry as string}
              </Form.Control.Feedback>
            )} */}
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group>
            <Form.Label>
              Type 
              {/* <span className="text-danger">*</span> */}
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Autofill"
              value={
                formik.values?.company?.gst_type ||
                formik.values?.gst_type ||
                ""
              }
              disabled
              isInvalid={!!(formik.errors.industry && formik.touched.industry)}
            />
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group>
            <Form.Label>
              Contact Person <span className="text-danger">*</span>
            </Form.Label>
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={formik.values?.company?.contacts || []}
              getOptionLabel={(option) => option.name || ""}
              isOptionEqualToValue={(
                option: ContactDetail,
                value: ContactDetail
              ) => String(option.contact_id) === String(value.contact_id)}
              value={contactPersonValue}
              onChange={(_, newValue) => {
                handleAutocompleteChange("contactPerson", newValue);
              }}
              onBlur={() => {
                formik.setFieldTouched("contactPerson", true);
              }}
              renderTags={() => null}
              renderOption={(props, option, { selected }) => (
                <li {...props} style={{ padding: 0 }}>
                  <Box
                    sx={{
                      px: 1.5,
                      py: 0.8,
                      width: "100%",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      cursor: "pointer",

                      // Hover
                      "&:hover": {
                        backgroundColor: MecordColors.Hover,
                        color: MecordColors.ActiveText,
                      },

                      // Selected
                      ...(selected && {
                        backgroundColor: MecordColors.ActiveBackground,
                        color: MecordColors.ActiveText,
                      }),
                    }}
                  >
                    {/* Contact name */}
                    <span>{option.name}</span>

                    {/* ❌ remove only if selected */}
                    {selected && (
                      <span
                        style={{
                          cursor: "pointer",
                          marginLeft: "10px",
                          color: "#d32f2f",
                          fontWeight: "bold",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();

                          const updated = contactPersonValue.filter(
                            (c: ContactDetail) =>
                              c.contact_id !== option.contact_id
                          );

                          handleAutocompleteChange("contactPerson", updated);
                        }}
                      >
                        ✕
                      </span>
                    )}
                  </Box>
                </li>
              )}
              ListboxProps={autocompleteDropdownStyles.ListboxProps}
              renderInput={(params) => (
                <BootstrapTextField
                  {...params}
                  placeholder="Select Contact Person"
                  size="small"
                  name="contactPerson"
                  sx={autocompleteDropdownStyles.inputSx}
                  error={
                    !!(
                      formik.errors.contactPerson &&
                      formik.touched.contactPerson
                    )
                  }
                  helperText={
                    formik.errors.contactPerson && formik.touched.contactPerson
                      ? (formik.errors.contactPerson as string)
                      : ""
                  }
                />
              )}
            />
            {/* Display selected contact persons as chips */}
            <div className="mt-2 d-flex flex-wrap gap-2">
              {contactPersonValue.map(
                (contact: ContactDetail, index: number) => (
                  <div
                    key={contact.contact_id || index}
                    className="d-flex align-items-center px-2 py-1 rounded"
                    style={{
                      background: MecordColors?.LightThemeBGColor,
                      border: `1px solid ${MecordColors?.ThemeBGColor}`,
                      fontSize: "14px",
                    }}
                  >
                    {contact.name || ""}
                    <span
                      style={{
                        marginLeft: "8px",
                        cursor: "pointer",
                        color: "red",
                      }}
                      onClick={() => {
                        const list = Array.isArray(formik.values.contactPerson)
                          ? formik.values.contactPerson
                          : [];
                        const updated = list.filter(
                          (c: ContactDetail) =>
                            c.contact_id !== contact.contact_id
                        );
                        handleAutocompleteChange("contactPerson", updated);
                      }}
                    >
                      ✕
                    </span>
                  </div>
                )
              )}
            </div>
          </Form.Group>
        </Col>
      </Row>
    </div>
  );
};

export default CustomerDetailsSection;
